/*
 * Settings → API Documentation Access (ApiDocUserController): logins for the
 * Swagger docs. As on the server, a password is "emailed" and never returned.
 */
import { ok, created, fail, notFound, invalid, nextId, daysAgo, hoursAgo, now } from "./util";

const DOCS_URL = "https://core.crm.demulla.co.ke";

const sets = [
  {
    key: "support-api",
    title: "DeeCRM360 Support API",
    description: "Raising and following support tickets from a connected system, and the signed webhooks.",
    url: `${DOCS_URL}/docs/support-api`,
  },
];

const users = [
  { id: 1, name: "Brian Otieno", email: "brian.otieno@example.co.ke", status: "active", sets: ["support-api"], last_signed_in_at: hoursAgo(3), password_sent_at: daysAgo(12), created_by_name: "Platform Admin", created_at: daysAgo(12) },
  { id: 2, name: "Faith Wanjiru", email: "faith@lms-partner.example", status: "active", sets: ["support-api"], last_signed_in_at: null, password_sent_at: daysAgo(1), created_by_name: "Platform Admin", created_at: daysAgo(1) },
  { id: 3, name: "Old Contractor", email: "contractor@example.com", status: "disabled", sets: ["support-api"], last_signed_in_at: daysAgo(60), password_sent_at: daysAgo(90), created_by_name: "Platform Admin", created_at: daysAgo(90) },
];

const keys = sets.map((s) => s.key);
const present = (u) => ({ ...u, set_titles: u.sets.map((k) => sets.find((s) => s.key === k)?.title).filter(Boolean) });
const find = (id) => users.find((u) => String(u.id) === String(id));
const setErrors = (list) => {
  if (!Array.isArray(list) || !list.length) return { sets: ["Tick at least one API documentation set."] };
  if (list.some((k) => !keys.includes(k))) return { sets: ["Choose from the API documentation sets listed."] };
  return null;
};

export default [
  ["get", "admin/api-docs-users", () => {
    const list = [...users].sort((a, b) => (a.status === b.status ? a.name.localeCompare(b.name) : a.status === "active" ? -1 : 1));
    return ok({ users: list.map(present), sets });
  }],
  ["post", "admin/api-docs-users", ({ body }) => {
    const errors = {};
    const email = String(body.email ?? "").trim().toLowerCase();
    if (!String(body.name ?? "").trim()) errors.name = ["The name field is required."];
    if (!email) errors.email = ["The email field is required."];
    else if (users.some((u) => u.email === email)) errors.email = ["There is already an API docs login for that email."];
    Object.assign(errors, setErrors(body.sets) ?? {});
    if (Object.keys(errors).length) return invalid(errors);
    const user = { id: nextId(users), name: body.name, email, status: "active", sets: keys.filter((k) => body.sets.includes(k)), last_signed_in_at: null, password_sent_at: now(), created_by_name: "Platform Admin", created_at: now() };
    users.push(user);
    return created(present(user), `${user.name} can now read the API documentation. Their login was emailed to ${user.email}.`);
  }],
  ["put", "admin/api-docs-users/:id", ({ params, body }) => {
    const u = find(params.id);
    if (!u) return notFound("API docs user was not found.");
    if (!String(body.name ?? "").trim()) return invalid({ name: ["The name field is required."] });
    const errors = setErrors(body.sets);
    if (errors) return invalid(errors);
    Object.assign(u, { name: body.name, sets: keys.filter((k) => body.sets.includes(k)) });
    return ok(present(u), `${u.name} saved`);
  }],
  ["post", "admin/api-docs-users/:id/password", ({ params }) => {
    const u = find(params.id);
    if (!u) return notFound("API docs user was not found.");
    if (u.status !== "active") return fail(`${u.name} is switched off. Switch them on before sending a new password.`, null, 409);
    u.password_sent_at = now();
    return ok(present(u), `A new password was emailed to ${u.email}. The old one no longer works.`);
  }],
  ["post", "admin/api-docs-users/:id/disable", ({ params }) => {
    const u = find(params.id);
    if (!u) return notFound("API docs user was not found.");
    u.status = "disabled";
    return ok(present(u), `${u.name} can no longer open the API documentation`);
  }],
  ["post", "admin/api-docs-users/:id/enable", ({ params }) => {
    const u = find(params.id);
    if (!u) return notFound("API docs user was not found.");
    u.status = "active";
    return ok(present(u), `${u.name} can open the API documentation again with their emailed password`);
  }],
  ["delete", "admin/api-docs-users/:id", ({ params }) => {
    const u = find(params.id);
    if (!u) return notFound("API docs user was not found.");
    users.splice(users.indexOf(u), 1);
    return ok({ id: u.id, name: u.name }, `${u.name} has been removed`);
  }],
];
